"use client";

import { rmcClient } from "@/lib/rmcClient";
import { roundMoney, roundToDecimals } from "@/lib/utils";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function fmtQty(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return roundToDecimals(n, 3).toFixed(3);
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return roundMoney(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function partyName(row, parties) {
  if (row?.party?.name) return row.party.name;
  const id = row?.party_id ?? row?.party?.id;
  return parties.find((p) => p.id === id)?.name ?? "—";
}

function materialName(row, materials) {
  if (Array.isArray(row?.items) && row.items.length) {
    return row.items.map((it) => it?.material?.name).filter(Boolean).join(", ");
  }
  if (row?.material?.name) return row.material.name;
  const id = row?.material_id ?? row?.material?.id;
  return materials.find((m) => m.id === id)?.name ?? "—";
}

function challanQty(row) {
  const items = Array.isArray(row?.items) ? row.items : [];
  if (items.length) {
    const total = items.reduce((sum, it) => {
      const qty = Number(it?.qty);
      return Number.isFinite(qty) ? sum + qty : sum;
    }, 0);
    if (Number.isFinite(total) && total > 0) return total;
  }
  const net = Number(row?.qty ?? row?.net_weight);
  if (Number.isFinite(net)) return net;
  return NaN;
}

function challanAmount(row) {
  if (row?.total_amount != null && row.total_amount !== "") {
    const t = Number(row.total_amount);
    if (Number.isFinite(t)) return t;
  }
  if (row?.sub_total != null && row.sub_total !== "") {
    const s = Number(row.sub_total);
    if (Number.isFinite(s)) return s;
  }
  if (row?.amount != null && row.amount !== "") {
    const a = Number(row.amount);
    if (Number.isFinite(a)) return a;
  }
  const net = Number(row?.qty ?? row?.net_weight);
  const rate = Number(row?.rate);
  if (Number.isFinite(net) && Number.isFinite(rate)) return net * rate;
  return NaN;
}

function computeTotals(rows) {
  let qty = 0;
  let amount = 0;
  for (const r of rows) {
    const q = challanQty(r);
    if (Number.isFinite(q)) qty += q;
    const a = challanAmount(r);
    if (Number.isFinite(a)) amount += a;
  }
  return {
    count: rows.length,
    qty: roundToDecimals(qty, 3),
    amount: roundMoney(amount),
  };
}

function filterPeriodLabel(snapshot) {
  const from = snapshot?.filters?.date_from;
  const to = snapshot?.filters?.date_to;
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Up to ${to}`;
  return "All dates";
}

const PurchaseChallansListPrint = forwardRef(function PurchaseChallansListPrint(_, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: `purchase-challans-${new Date().toISOString().slice(0, 10)}`,
  });

  useImperativeHandle(ref, () => ({
    openPrint: async ({ filters = {}, parties = [], materials = [] } = {}) => {
      const payload = (
        await rmcClient.listPurchaseChallans({
          ...(filters.party_id ? { party_id: filters.party_id } : {}),
          ...(filters.material_id ? { material_id: filters.material_id } : {}),
          ...(filters.purchase_kind ? { purchase_kind: filters.purchase_kind } : {}),
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
          pagination: false,
        })
      ).data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
      flushSync(() => {
        setSnapshot({
          rows,
          filters,
          parties,
          materials,
          totals: computeTotals(rows),
          printedAt: new Date().toLocaleString("en-IN"),
        });
      });
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  const companyName = company?.name || company?.company_name || "";
  const companyBlessing = (company?.blessing || company?.invoice_blessing || "").trim();
  const rows = snapshot?.rows ?? [];
  const parties = snapshot?.parties ?? [];
  const materials = snapshot?.materials ?? [];
  const totals = snapshot?.totals ?? { count: 0, qty: 0, amount: 0 };

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white p-6 text-black print:block">
        <div className="mx-auto max-w-[297mm]">
          {companyBlessing ? (
            <p className="mb-2 text-center text-xs italic text-slate-700">{companyBlessing}</p>
          ) : null}
          {companyName ? (
            <p className="mb-1 text-center text-sm font-bold">{companyName}</p>
          ) : null}
          <h1 className="mb-1 text-center text-base font-bold">Purchase challans</h1>
          <p className="mb-1 text-center text-xs text-slate-700">{filterPeriodLabel(snapshot)}</p>
          <p className="mb-4 text-center text-[10px] text-slate-600">
            Printed {snapshot?.printedAt ?? ""} · {totals.count} record{totals.count === 1 ? "" : "s"}
          </p>

          <table className="mb-4 w-full border-collapse border border-slate-800 text-[10px]">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">Challan No</th>
                <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">Date</th>
                <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">Party</th>
                <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">Material</th>
                <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">Kind</th>
                <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">Qty</th>
                <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">Rate</th>
                <th className="border border-slate-800 px-1.5 py-1 text-right font-semibold">Amount</th>
                <th className="border border-slate-800 px-1.5 py-1 text-left font-semibold">Vehicle</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="border border-slate-800 px-2 py-2 text-slate-600">
                    No records
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const qty = challanQty(row);
                  const amt = challanAmount(row);
                  return (
                    <tr key={row.id ?? `${row.challan_no}-${row.date}`}>
                      <td className="border border-slate-800 px-1.5 py-1">{row.challan_no ?? "—"}</td>
                      <td className="border border-slate-800 px-1.5 py-1">{row.date ?? "—"}</td>
                      <td className="border border-slate-800 px-1.5 py-1">{partyName(row, parties)}</td>
                      <td className="border border-slate-800 px-1.5 py-1">{materialName(row, materials)}</td>
                      <td className="border border-slate-800 px-1.5 py-1">
                        {row.purchase_kind === "CONCRETE" ? "Concrete" : row.purchase_kind === "REGULAR" ? "Regular" : row.purchase_kind ?? "—"}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {Number.isFinite(qty) ? fmtQty(qty) : "—"}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {row.rate != null ? fmtMoney(row.rate) : "—"}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1 text-right tabular-nums">
                        {Number.isFinite(amt) ? fmtMoney(amt) : "—"}
                      </td>
                      <td className="border border-slate-800 px-1.5 py-1">{row.vehicle_no ?? "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 font-semibold">
                  <td colSpan={5} className="border border-slate-800 px-1.5 py-1.5 text-right">
                    Totals
                  </td>
                  <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                    {fmtQty(totals.qty)}
                  </td>
                  <td className="border border-slate-800 px-1.5 py-1.5" />
                  <td className="border border-slate-800 px-1.5 py-1.5 text-right tabular-nums">
                    ₹{fmtMoney(totals.amount)}
                  </td>
                  <td className="border border-slate-800 px-1.5 py-1.5" />
                </tr>
              </tfoot>
            )}
          </table>

          <div className="grid grid-cols-3 gap-3 border border-slate-800 p-3 text-xs">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Challans</p>
              <p className="mt-1 text-lg font-bold tabular-nums">{totals.count}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Total quantity</p>
              <p className="mt-1 text-lg font-bold tabular-nums">{fmtQty(totals.qty)}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Total amount</p>
              <p className="mt-1 text-lg font-bold tabular-nums">₹{fmtMoney(totals.amount)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PurchaseChallansListPrint.displayName = "PurchaseChallansListPrint";

export default PurchaseChallansListPrint;
